import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { studentV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { evidenceApi, exportApi, sectionsApi } from '../../../services/apiV2';
import { Evidence } from '../../../types/v2';

export const V2EvidenceLedger: React.FC = () => {
  const { hookId } = useParams();
  const redesignEnabled = isUiRedesignPageEnabled('v2_student_evidence_ledger');
  const [items, setItems] = useState<Evidence[]>([]);
  const [exporting, setExporting] = useState(false);
  const shellActions = (
    <Link
      to="/v2/student"
      className={redesignEnabled
        ? 'rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-muted hover:bg-surface-alt hover:text-text'
        : 'text-sm text-indigo-600 hover:text-indigo-800'}
    >
      Back to dashboard
    </Link>
  );
  const load = async () => {
    if (!hookId) return;
    const response = await evidenceApi.list(Number(hookId));
    setItems(response.data?.evidence || response.data || []);
  };

  useEffect(() => {
    load();
  }, [hookId]);

  const applyEvidenceApproved = useMemo(() => {
    const applyEvidence = items.find((item) => item.stage === 'apply');
    return applyEvidence?.status === 'approved';
  }, [items]);

  const exportPackage = async () => {
    if (!hookId || !applyEvidenceApproved) return;
    setExporting(true);
    const sections = await sectionsApi.list();
    const sectionId = sections.data?.[0]?.id;
    if (!sectionId) {
      setExporting(false);
      return;
    }
    const response = await exportApi.generate({ hook: Number(hookId), section: sectionId });
    const fileResponse = await exportApi.download(response.data.export_id);
    const blob = new Blob([fileResponse.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
    setExporting(false);
  };

  const ledgerSurface = (
    <div className={redesignEnabled ? 'max-w-5xl mx-auto' : 'max-w-5xl mx-auto p-6'}>
      {!redesignEnabled && shellActions}
      <h1 className="text-2xl font-semibold text-gray-900">Evidence Ledger</h1>
      <div className="mt-4 space-y-3">
        {items.map((item) => (
          <div key={item.id} className="bg-white rounded shadow p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Stage: {item.stage}</p>
              <span className={`text-xs px-2 py-1 rounded-full ${item.status === 'approved' ? 'bg-green-100 text-green-700' : item.status === 'needs_revision' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>
                {item.status}
              </span>
            </div>
            <pre className="text-xs bg-gray-50 p-2 rounded mt-2">{JSON.stringify(item.content, null, 2)}</pre>
          </div>
        ))}
      </div>
      <button
        className={`mt-4 px-4 py-2 rounded ${applyEvidenceApproved ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
        onClick={exportPackage}
        disabled={!applyEvidenceApproved || exporting}
      >
        {exporting ? 'Exporting…' : 'Export for Headrush'}
      </button>
    </div>
  );

  return (
    <OptionalAppShell
      enabled={redesignEnabled}
      role="student"
      navItems={studentV2NavItems('dashboard')}
      actions={shellActions}
      title="Evidence Ledger"
    >
      {ledgerSurface}
    </OptionalAppShell>
  );
};
